import { useEffect, useState } from 'react';
import { Button, Card, Dropdown, Popconfirm, Space, Spin, Tag, Typography, message } from 'antd';
import { useList } from '@refinedev/core';
import type { CompanyPromptRow, CoreVersion, PromptPipeline, PromptTemplate } from '@viq-admin/types';
import {
  PIPELINE_LABELS,
  getCompanyPrompts,
  repinCompany,
  listCoreVersions,
  detachTemplate,
  applyTemplate,
} from '../../api/promptStore';
import { Can } from '../../ability/AbilityContext';
import { space } from '@viq-admin/ui';

interface Props {
  companyId: string;
}

/**
 * Company detail's AI-Prompts section: one Card per pipeline (one row per
 * `getCompanyPrompts` entry), showing this company's pinned core version
 * (+ a "behind active" flag once the org's active version has moved on
 * without this company being repinned) and its custom-rules/append state,
 * with per-company controls to repin the core version or add/clear a
 * custom-rules template.
 *
 * Self-contained (owns its own fetch) and injected directly into
 * `CompanyDetail`'s read view — NOT a route of its own, so it isn't in
 * `consoleCapabilities.ts`'s DataTable/RowActionsMenu registry. Fetch
 * pattern mirrors `AppliedConferencesModal`/`CoreVersionsDrawer`: bare
 * useEffect + useState + `message.error` — the custom
 * `/v1/superadmin/companies/:id/prompts` endpoint has no corresponding
 * Refine resource. The append-template picker DOES have a Refine resource
 * (`prompt-templates`, global/scope-exempt — see dataProvider.ts), so that
 * one uses `useList` and filters client-side to each row's pipeline, same
 * as `PromptRolloutList`.
 *
 * "Change" and "Add" are both a button that opens a click-triggered AntD
 * `Dropdown` menu — the same trigger-button-opens-a-menu idiom as
 * `RowActionsMenu`'s kebab — rather than a bordered `<Select>` box: "Core
 * version N" / "None" stays plain text, and picking a menu item fires the
 * mutation immediately (no separate confirm step). "Clear" IS wrapped in a
 * `Popconfirm`, matching `AppliedCompaniesModal`'s / `AppliedConferencesModal`'s
 * per-row "Detach" convention for a destructive single-row action.
 *
 * `CompanyPromptRow` doesn't embed its pipeline's version catalogue, so
 * core versions are fetched once per pipeline present in the row set,
 * chained after `getCompanyPrompts` resolves. Neither that fetch nor the
 * `prompt-templates` list needs re-running after a mutation — repin/detach/
 * apply change which version or template is ATTACHED to this company, not
 * the version catalogue or the template roster itself — so `refresh()`
 * (called after every mutation) only re-fetches `rows`.
 *
 * Gated on `manage Company` — NOT `manage CorePrompt` / `manage
 * PromptTemplate`, which gate the global prompt-console screens — because
 * this is a per-company action surface; it rides the same capability as
 * every other company-scoped write on this page (e.g. Archive/Restore in
 * `CompanyDetail` itself). Read-only supers see the state with no controls.
 */
export function CompanyPromptsSection({ companyId }: Props) {
  const [rows, setRows] = useState<CompanyPromptRow[]>([]);
  const [versionsByPipeline, setVersionsByPipeline] = useState<
    Partial<Record<PromptPipeline, CoreVersion[]>>
  >({});
  const [loading, setLoading] = useState(false);
  const [repinningPipeline, setRepinningPipeline] = useState<PromptPipeline | null>(null);
  const [clearingPipeline, setClearingPipeline] = useState<PromptPipeline | null>(null);
  const [addingPipeline, setAddingPipeline] = useState<PromptPipeline | null>(null);

  const { result: templatesResult } = useList<PromptTemplate>({ resource: 'prompt-templates' });

  function refresh() {
    return getCompanyPrompts(companyId).then(setRows);
  }

  useEffect(() => {
    setLoading(true);
    getCompanyPrompts(companyId)
      .then(async (promptRows) => {
        setRows(promptRows);
        // Fetch each distinct pipeline's version catalogue once, so every
        // row's "Change" menu is ready without a per-click fetch.
        const pipelines = Array.from(new Set(promptRows.map((r) => r.pipeline)));
        const pairs = await Promise.all(
          pipelines.map(
            async (p): Promise<[PromptPipeline, CoreVersion[]]> => [p, await listCoreVersions(p)],
          ),
        );
        const map: Partial<Record<PromptPipeline, CoreVersion[]>> = {};
        for (const [p, versions] of pairs) map[p] = versions;
        setVersionsByPipeline(map);
      })
      .catch((err) =>
        message.error((err as { message?: string }).message ?? 'Failed to load AI prompts'),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function handleRepin(pipeline: PromptPipeline, versionId: string) {
    setRepinningPipeline(pipeline);
    try {
      await repinCompany(companyId, pipeline, versionId);
      message.success('Core version updated');
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to update core version');
    } finally {
      setRepinningPipeline(null);
    }
  }

  async function handleClear(row: CompanyPromptRow) {
    if (!row.sourceTemplateId) {
      // Defensive: deleting a still-applied template is now refused server-side
      // (409), so an append without provenance should be unreachable. If one
      // exists anyway (pre-existing data), say so — silently returning here left
      // the operator clicking a Clear button that did nothing at all.
      message.error(
        'These custom rules have no source template, so they cannot be cleared from here. Apply a template to this pipeline, then clear it.',
      );
      return;
    }
    setClearingPipeline(row.pipeline);
    try {
      await detachTemplate(row.sourceTemplateId, companyId);
      message.success('Custom rules cleared');
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to clear custom rules');
    } finally {
      setClearingPipeline(null);
    }
  }

  async function handleAdd(pipeline: PromptPipeline, templateId: string) {
    setAddingPipeline(pipeline);
    try {
      await applyTemplate(templateId, [companyId]);
      message.success('Custom rules added');
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to add custom rules');
    } finally {
      setAddingPipeline(null);
    }
  }

  return (
    <div data-testid="company-prompts-section">
      <Typography.Title level={5}>AI Prompts</Typography.Title>
      {loading ? (
        <Spin />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {rows.map((row) => (
            <Card
              key={row.pipeline}
              size="small"
              title={PIPELINE_LABELS[row.pipeline]}
              data-testid={`company-prompt-${row.pipeline}`}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}
                >
                  <Typography.Text>{`Core version ${row.pinnedVersion}`}</Typography.Text>
                  {row.isBehindActive && <Tag color="red">behind active</Tag>}
                  <Can I="manage" a="Company" passThrough>
                    {(allowed: boolean) =>
                      allowed ? (
                        <Dropdown
                          trigger={['click']}
                          menu={{
                            items: (versionsByPipeline[row.pipeline] ?? []).map((v) => ({
                              key: v.id,
                              label: `Version ${v.version}${v.isActive ? ' (active)' : ''}`,
                              onClick: () => handleRepin(row.pipeline, v.id),
                            })),
                          }}
                        >
                          <Button size="small" loading={repinningPipeline === row.pipeline}>
                            Change ▾
                          </Button>
                        </Dropdown>
                      ) : null
                    }
                  </Can>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}
                >
                  <Typography.Text type="secondary">Custom rules:</Typography.Text>
                  {row.hasAppend ? (
                    <>
                      <Typography.Text>Yes —</Typography.Text>
                      <Typography.Text strong>
                        {row.sourceTemplateName ?? 'custom (source deleted)'}
                      </Typography.Text>
                      <Can I="manage" a="Company" passThrough>
                        {(allowed: boolean) =>
                          allowed ? (
                            <Popconfirm
                              title="Clear custom rules for this pipeline?"
                              onConfirm={() => handleClear(row)}
                            >
                              <Button
                                size="small"
                                danger
                                loading={clearingPipeline === row.pipeline}
                              >
                                Clear
                              </Button>
                            </Popconfirm>
                          ) : null
                        }
                      </Can>
                    </>
                  ) : (
                    <>
                      <Typography.Text>None</Typography.Text>
                      <Can I="manage" a="Company" passThrough>
                        {(allowed: boolean) =>
                          allowed ? (
                            <Dropdown
                              trigger={['click']}
                              menu={{
                                items: (templatesResult?.data ?? [])
                                  .filter((t) => t.pipeline === row.pipeline)
                                  .map((t) => ({
                                    key: t.id,
                                    label: t.name,
                                    onClick: () => handleAdd(row.pipeline, t.id),
                                  })),
                              }}
                            >
                              <Button size="small" loading={addingPipeline === row.pipeline}>
                                Add
                              </Button>
                            </Dropdown>
                          ) : null
                        }
                      </Can>
                    </>
                  )}
                </div>
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}
